/**
 * Stan „trwa aktualizacja systemu” panelu tenanta (ADR-0113b §5).
 *
 * Baza tenanta na serwerze klienta nie przeszła aktualizacji przy wdrożeniu — API odpowiada
 * na każde żądanie tenanta `503 { error: 'TENANT_UPDATING' }`. Przechwytywanie odpowiedzi
 * (`lib/api.ts`) ustawia tu flagę; `App.tsx` pokazuje wtedy ekran „Trwa aktualizacja systemu”
 * zamiast tras — bez wylogowania i bez pętli odświeżania sesji (sesja zostaje ważna; po
 * dociągnięciu wystarczy „Odśwież”).
 *
 * Stan poza Reactem (axios), bo interceptor żyje poza drzewem komponentów; React czyta go
 * przez `useTenantUpdating()` (useSyncExternalStore).
 */

import { useSyncExternalStore } from 'react';
import { isAxiosError } from 'axios';

export const TENANT_UPDATING = 'TENANT_UPDATING';

let updating = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** Odpowiedź API „tenant w trakcie aktualizacji bazy” (503 TENANT_UPDATING). */
export function isTenantUpdatingError(err: unknown): boolean {
  if (!isAxiosError(err)) return false;
  const data = err.response?.data as { error?: unknown } | undefined;
  return err.response?.status === 503 && data?.error === TENANT_UPDATING;
}

export function markTenantUpdating(): void {
  if (updating) return;
  updating = true;
  emit();
}

export function clearTenantUpdating(): void {
  if (!updating) return;
  updating = false;
  emit();
}

export function isTenantUpdating(): boolean {
  return updating;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTenantUpdating(): boolean {
  return useSyncExternalStore(subscribe, isTenantUpdating, isTenantUpdating);
}
